import React, { createContext, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchTranslationDictionary } from "../actions";

// Create context for translations
const TranslationContext = createContext({});

/**
 * Translation Provider Component
 * Provides translations to child components with lazy loading.
 * Translations are only fetched when actually needed (on first access).
 */
export const TranslationProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dictionaryFromDb = useSelector(
    (state) => state.translation?.dictionary
  );

  // Lazy load translations on first access
  const fetchTranslationsIfNeeded = React.useCallback(async () => {
    if (!isLoaded && !isLoading && (!dictionaryFromDb || Object.keys(dictionaryFromDb).length === 0)) {
      setIsLoading(true);
      try {
        await dispatch(
          fetchTranslationDictionary((dictionary) => {
            setIsLoaded(true);
          })
        );
      } catch (error) {
        console.error("Failed to load translations:", error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [isLoaded, isLoading, dictionaryFromDb, dispatch]);

  return (
    <TranslationContext.Provider
      value={{
        dictionary: dictionaryFromDb || {},
        isLoaded: isLoaded || (dictionaryFromDb && Object.keys(dictionaryFromDb).length > 0),
        isLoading,
        fetchTranslationsIfNeeded
      }}
    >
      {children}
    </TranslationContext.Provider>
  );
};

/**
 * Hook to access translation dictionary
 * Triggers lazy loading on first use
 * @returns {Object} - { dictionary, getTranslation, isLoaded, isLoading }
 */
export const useTranslationDictionary = () => {
  const context = useContext(TranslationContext);

  // Trigger lazy loading on first access
  const { fetchTranslationsIfNeeded } = context || {};
  React.useEffect(() => {
    if (fetchTranslationsIfNeeded) {
      fetchTranslationsIfNeeded();
    }
  }, [fetchTranslationsIfNeeded]);

  if (!context) {
    return {
      dictionary: {},
      getTranslation: () => null,
      getTranslationText: () => "",
      getPartOfSpeech: () => "",
      isLoaded: false,
      isLoading: false
    };
  }

  /**
   * Get translation for a word
   * @param {string} word - English word to translate
   * @returns {Object|string|Array|null} - Translation data or null if not found
   */
  const getTranslation = (word) => {
    if (!word) return null;

    const lowerWord = word.toLowerCase();
    const translation = context.dictionary[lowerWord];

    return translation || null;
  };

  /**
   * Get formatted translation text
   * @param {string} word - English word to translate
   * @returns {string} - Formatted translation string
   */
  const getTranslationText = (word) => {
    const translation = getTranslation(word);

    if (!translation) return "";

    // Handle new "meanings" array structure from database
    if (
      translation &&
      typeof translation === "object" &&
      translation.meanings &&
      Array.isArray(translation.meanings)
    ) {
      return translation.meanings
        .flatMap((m) => m.translations || [])
        .filter(Boolean)
        .join("; ");
    }

    // Handle different legacy translation formats
    if (typeof translation === "string") {
      return translation;
    } else if (Array.isArray(translation)) {
      return translation.join("; ");
    } else if (typeof translation === "object") {
      if (translation.translation) {
        return translation.translation;
      } else if (
        translation.translations &&
        Array.isArray(translation.translations)
      ) {
        return translation.translations.join("; ");
      }
    }

    return "";
  };

  /**
   * Get part of speech for a word
   * @param {string} word - English word
   * @returns {string} - Part of speech or empty string
   */
  const getPartOfSpeech = (word) => {
    const translation = getTranslation(word);

    if (translation && typeof translation === "object") {
      // Handle new "meanings" array structure
      if (translation.meanings && translation.meanings.length > 0) {
        return translation.meanings[0].pos || "";
      }
      // Handle legacy object format
      if (translation.pos) {
        return translation.pos;
      }
    }

    return "";
  };

  return {
    dictionary: context.dictionary,
    getTranslation,
    getTranslationText,
    getPartOfSpeech,
    isLoaded: context.isLoaded,
    isLoading: context.isLoading || false
  };
};

/**
 * Higher-order component to inject translation dictionary
 * @param {React.Component} Component - Component to wrap
 * @returns {React.Component} - Wrapped component with translations prop
 */
export const withTranslations = (Component) => {
  return (props) => {
    const translations = useTranslationDictionary();
    return <Component {...props} translations={translations} />;
  };
};
